import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  ShoppingCart, RefreshCw, AlertCircle, CheckCircle2,
  XCircle, ChevronDown, ChevronUp, Package, Building2,
  Loader2, Play, Hash
} from 'lucide-react';
import { toast } from 'sonner';

const OrdenCompraPage = () => {
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [proveedores, setProveedores] = useState([]);
  const [totalProductos, setTotalProductos] = useState(0);

  // Estado por proveedor: { [provId]: 'idle' | 'loading' | 'ok' | 'error' }
  const [estados, setEstados] = useState({});
  // Resultado por proveedor: { [provId]: { orden_id, nro_oc, productos_enviados, error } }
  const [resultados, setResultados] = useState({});

  // Expansión de filas de detalle
  const [expandidos, setExpandidos] = useState({});

  const fetchProveedores = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/orden-compra/productos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      setProveedores(data.proveedores || []);
      setTotalProductos(data.total_productos || 0);

      // Inicializar estados en idle
      const init = {};
      (data.proveedores || []).forEach(p => { init[p.MATE_ID_PROV] = 'idle'; });
      setEstados(init);
      setResultados({});
    } catch (err) {
      setError(err.message);
      toast.error('Error al cargar proveedores: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchProveedores();
  }, [fetchProveedores]);

  const crearOrden = async (provId, razonSocial) => {
    setEstados(prev => ({ ...prev, [provId]: 'loading' }));
    try {
      const res = await fetch(`${API_URL}/api/admin/orden-compra/crear/${provId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || `Error ${res.status}`);
      }

      setEstados(prev => ({ ...prev, [provId]: 'ok' }));
      setResultados(prev => ({
        ...prev,
        [provId]: {
          orden_id: data.orden_id,
          nro_oc: data.nro_oc,
          productos_enviados: data.productos_enviados
        }
      }));
      toast.success(`OC #${data.nro_oc} creada para ${razonSocial} (${data.productos_enviados} ítems)`);
    } catch (err) {
      setEstados(prev => ({ ...prev, [provId]: 'error' }));
      setResultados(prev => ({ ...prev, [provId]: { error: err.message } }));
      toast.error(`Error en ${razonSocial}: ${err.message}`);
    }
  };

  const crearTodasLasOrdenes = async () => {
    const pendientes = proveedores.filter(p => estados[p.MATE_ID_PROV] === 'idle');
    if (pendientes.length === 0) {
      toast.info('No hay órdenes pendientes de crear.');
      return;
    }
    for (const prov of pendientes) {
      await crearOrden(prov.MATE_ID_PROV, prov.CLIE_RAZON_SOCIAL);
    }
  };

  const toggleExpand = (provId) => {
    setExpandidos(prev => ({ ...prev, [provId]: !prev[provId] }));
  };

  const contadorEstados = proveedores.reduce((acc, p) => {
    const est = estados[p.MATE_ID_PROV] || 'idle';
    acc[est] = (acc[est] || 0) + 1;
    return acc;
  }, {});

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="bg-red-900/20 border border-red-500/50 rounded-xl p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-red-200 mb-2">Error de Conexión</h2>
          <p className="text-red-300/80 mb-6">{error}</p>
          <button
            onClick={fetchProveedores}
            className="bg-red-600 hover:bg-red-500 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="text-gray-100">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* ── Header ── */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 bg-[#1A1D27] p-6 rounded-xl border border-gray-800 shadow-xl">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-[#00a1df] to-blue-400 bg-clip-text text-transparent flex items-center gap-3">
              <ShoppingCart className="w-7 h-7 text-[#00a1df]" />
              Órdenes de Compra
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Generación automática de OC por proveedor según reposición de stock
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={crearTodasLasOrdenes}
              disabled={loading || proveedores.every(p => estados[p.MATE_ID_PROV] !== 'idle')}
              className="flex items-center gap-2 bg-[#00a1df] hover:bg-[#0090c7] disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-lg shadow-lg transition-all font-black text-xs active:scale-95"
            >
              <Play className="w-4 h-4 fill-current" />
              CREAR TODAS
            </button>
            <button
              onClick={fetchProveedores}
              disabled={loading}
              className="flex items-center justify-center w-10 h-10 bg-gray-800 hover:bg-gray-700 text-[#00a1df] border border-gray-700 rounded-lg transition-all"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* ── Stats ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Proveedores"
            value={loading ? '…' : proveedores.length}
            icon={<Building2 className="w-5 h-5" />}
            color="text-[#00a1df]"
            bg="bg-[#00a1df]/10"
          />
          <StatCard
            label="Total Productos"
            value={loading ? '…' : totalProductos.toLocaleString()}
            icon={<Package className="w-5 h-5" />}
            color="text-purple-400"
            bg="bg-purple-500/10"
          />
          <StatCard
            label="OC Creadas"
            value={loading ? '…' : (contadorEstados.ok || 0)}
            icon={<CheckCircle2 className="w-5 h-5" />}
            color="text-green-400"
            bg="bg-green-500/10"
          />
          <StatCard
            label="Con Error"
            value={loading ? '…' : (contadorEstados.error || 0)}
            icon={<XCircle className="w-5 h-5" />}
            color="text-red-400"
            bg="bg-red-500/10"
          />
        </div>

        {/* ── Tabla de proveedores ── */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-2xl">
          {/* Cabecera */}
          <div className="p-4 border-b border-gray-700 bg-gray-800/50">
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#00a1df]" />
              Proveedores con reposición pendiente
            </h3>
          </div>

          {loading ? (
            <div className="py-20 flex flex-col items-center gap-4 text-gray-500">
              <Loader2 className="w-8 h-8 animate-spin text-[#00a1df]" />
              <p className="text-sm">Cargando datos de reposición...</p>
            </div>
          ) : proveedores.length === 0 ? (
            <div className="py-20 text-center text-gray-500">
              <Package className="w-10 h-10 mx-auto mb-3 opacity-20" />
              <p>No hay productos para reponer.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-700/50">
              {proveedores.map((prov) => {
                const provId = prov.MATE_ID_PROV;
                const estado = estados[provId] || 'idle';
                const resultado = resultados[provId];
                const isExpanded = expandidos[provId];

                return (
                  <div key={provId}>
                    {/* Fila principal */}
                    <div className="grid grid-cols-[2fr,1fr,1fr,auto,auto] items-center px-6 py-4 hover:bg-gray-700/20 transition-colors gap-4">

                      {/* Proveedor */}
                      <div className="min-w-0">
                        <p className="font-bold text-white truncate">{prov.CLIE_RAZON_SOCIAL}</p>
                        <p className="text-[10px] text-gray-500 font-mono uppercase">ID: {provId}</p>
                      </div>

                      {/* Productos */}
                      <div className="text-center">
                        <p className="text-lg font-bold text-white">{prov.total_productos}</p>
                        <p className="text-[10px] text-gray-500 uppercase tracking-wider">artículos</p>
                      </div>

                      {/* Resultado / Estado */}
                      <div>
                        {estado === 'idle' && (
                          <span className="text-xs text-gray-500 italic">— pendiente —</span>
                        )}
                        {estado === 'loading' && (
                          <span className="flex items-center gap-1.5 text-xs text-[#00a1df]">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Creando...
                          </span>
                        )}
                        {estado === 'ok' && resultado && (
                          <div>
                            <span className="flex items-center gap-1 text-xs text-green-400 font-bold">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              OC #{resultado.nro_oc}
                            </span>
                            <span className="text-[10px] text-gray-500">
                              {resultado.productos_enviados} ítems · ID {resultado.orden_id}
                            </span>
                          </div>
                        )}
                        {estado === 'error' && resultado && (
                          <div>
                            <span className="flex items-center gap-1 text-xs text-red-400 font-bold">
                              <XCircle className="w-3.5 h-3.5" />
                              Error
                            </span>
                            <span className="text-[10px] text-red-400/70 truncate block max-w-[140px]">
                              {resultado.error}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Botón crear */}
                      <button
                        onClick={() => crearOrden(provId, prov.CLIE_RAZON_SOCIAL)}
                        disabled={estado === 'loading' || estado === 'ok'}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-black transition-all active:scale-95 ${
                          estado === 'ok'
                            ? 'bg-green-900/30 text-green-400 border border-green-500/20 cursor-default'
                            : estado === 'loading'
                            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                            : estado === 'error'
                            ? 'bg-red-600 hover:bg-red-500 text-white'
                            : 'bg-[#00a1df] hover:bg-[#0090c7] text-white shadow-lg shadow-[#00a1df]/20'
                        }`}
                      >
                        {estado === 'loading' ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : estado === 'ok' ? (
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        ) : (
                          <ShoppingCart className="w-3.5 h-3.5" />
                        )}
                        {estado === 'ok' ? 'Creada' : estado === 'error' ? 'Reintentar' : 'Crear OC'}
                      </button>

                      {/* Toggle detalle */}
                      <button
                        onClick={() => toggleExpand(provId)}
                        className="p-2 text-gray-500 hover:text-gray-300 rounded-lg hover:bg-gray-700/40 transition-colors"
                        title="Ver productos"
                      >
                        {isExpanded
                          ? <ChevronUp className="w-4 h-4" />
                          : <ChevronDown className="w-4 h-4" />
                        }
                      </button>
                    </div>

                    {/* Detalle expandible */}
                    {isExpanded && (
                      <div className="bg-gray-900/60 border-t border-gray-700/50 px-6 py-4">
                        <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold mb-3 flex items-center gap-2">
                          <Package className="w-3 h-3" />
                          Productos a reponer ({prov.total_productos})
                        </p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-gray-500 uppercase tracking-wider border-b border-gray-700/50">
                                <th className="text-left py-2 pr-4 font-bold">SKU</th>
                                <th className="text-left py-2 pr-4 font-bold">Nombre</th>
                                <th className="text-right py-2 pr-4 font-bold">Cant. a Reponer</th>
                                <th className="text-right py-2 font-bold">CRM Neto</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800">
                              {prov.productos.map((prod) => (
                                <tr key={prod.ID} className="hover:bg-gray-800/40 transition-colors">
                                  <td className="py-2 pr-4 font-mono text-cyan-400">{prod.MATE_CODIGO}</td>
                                  <td className="py-2 pr-4 text-gray-300 max-w-xs truncate" title={prod.MATE_NOMBRE}>
                                    {prod.MATE_NOMBRE}
                                  </td>
                                  <td className="py-2 pr-4 text-right font-bold text-white">
                                    {prod.MATE_CANT_PARA_REPOSICION?.toLocaleString()}
                                  </td>
                                  <td className="py-2 text-right text-gray-400">
                                    {prod.MATE_CRM
                                      ? `$${prod.MATE_CRM.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                      : '—'
                                    }
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Info Footer ── */}
        <div className="bg-blue-900/10 border border-blue-500/20 rounded-lg p-4 flex gap-3 text-sm text-blue-300/80">
          <Hash className="w-5 h-5 shrink-0 mt-0.5 text-blue-400" />
          <p>
            Los datos provienen de la smartie de reposición de stock (Schema <strong className="text-blue-300">1383</strong> · Smartie <strong className="text-blue-300">3347</strong>).
            Cada botón crea una Orden de Compra en YiQi con todos los artículos del proveedor
            usando <code className="text-blue-300 bg-blue-900/30 px-1 rounded">MATE_CANT_PARA_REPOSICION</code> como cantidad
            y <code className="text-blue-300 bg-blue-900/30 px-1 rounded">MATE_CRM</code> como precio unitario neto.
          </p>
        </div>

      </div>
    </div>
  );
};

// ── Componente auxiliar ──────────────────────────────────────────────────────
const StatCard = ({ label, value, icon, color, bg }) => (
  <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5 backdrop-blur-sm">
    <div className="flex justify-between items-start">
      <div>
        <p className="text-gray-400 text-sm">{label}</p>
        <p className={`text-3xl font-bold mt-1 ${color}`}>{value}</p>
      </div>
      <div className={`${bg} p-2 rounded-lg ${color}`}>{icon}</div>
    </div>
  </div>
);

export default OrdenCompraPage;
